import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Keyboard, KeyboardResize } from '@capacitor/keyboard';
import { isNative, platform } from './is-native';
import { KEYBOARD_EVENT, createKeyboardTracker } from './keyboard';
import { setupPushToStartTokens } from './live-activity';
import { reconcileEntitlementOnLaunch, hasIapPlugin } from './iap';
import { NATIVE_APP_COOKIE, nativeAppCookieValue } from '@/lib/billing/native-app-cookie';
import { warmUpTapToPay } from './tap-to-pay';
import { backButtonAction } from './back-swipe';
import { sheetStackDepth } from '@/components/ui/sheet-stack';

let booted = false;

export async function bootstrapNative(router: { back: () => void }): Promise<void> {
  // Signal to the inline white-screen watchdog (see src/app/layout.tsx head)
  // that the React app has mounted and hydrated. This fires from a useEffect,
  // so reaching here proves the bundle loaded and ran — the watchdog only
  // takes over the screen when this flag is still unset after its timeout.
  // Set before the `booted` guard so a second call still (idempotently) marks it.
  if (typeof window !== 'undefined') {
    (window as unknown as { __muckaBooted?: boolean }).__muckaBooted = true;
  }

  if (booted) return;
  booted = true;

  document.documentElement.classList.add(`platform-${platform()}`);

  // Keyboard height tracking — expose as a CSS variable so surfaces can
  // opt into lifting above the keyboard. Two sources feed it:
  //   1. Capacitor Keyboard plugin events (native only — authoritative)
  //   2. visualViewport resize (web + native fallback — covers Android
  //      WebView versions where the Capacitor plugin under-reports)
  // Setting up outside the `isNative()` guard means web PWA users get the
  // lift behaviour for free. How the two are combined — and why it must stay
  // max() rather than last-writer-wins — is in createKeyboardTracker.
  const root = document.documentElement;
  const keyboard = createKeyboardTracker((height) => {
    root.style.setProperty('--mucka-keyboard-h', `${height}px`);
    if (height > 0) root.classList.add('keyboard-open');
    else root.classList.remove('keyboard-open');
    // Announce it, so surfaces that need to do more than restyle can react —
    // the Mucka Bar re-pins its transcript, because the sheet gets shorter and
    // a scroller keeps its scrollTop, which would leave the newest message
    // below the fold exactly when you have just typed.
    window.dispatchEvent(new CustomEvent(KEYBOARD_EVENT, { detail: { height } }));
  });
  if (typeof window !== 'undefined' && window.visualViewport) {
    const vv = window.visualViewport;
    const update = () => {
      keyboard.setViewport(window.innerHeight - vv.height - vv.offsetTop);
    };
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
  }

  if (!isNative()) return;

  document.documentElement.classList.add('is-native');

  // Tag every same-origin fetch with the platform header so server
  // routes can refuse to serve App-Store-sensitive endpoints (anything
  // taking payment) from inside the native binary. Pairs with
  // src/lib/billing/native-guard.ts on the server.
  injectPlatformHeader();
  stampNativeAppCookie();

  try {
    // Dark = dark text/icons (correct for our light --surface2 bg).
    await StatusBar.setStyle({ style: Style.Dark });
    // Overlay mode: WebView extends behind the status bar. The body's
    // background colour shows through the status-bar zone, and CSS
    // `env(safe-area-inset-top)` keeps content out from under the notch.
    // setBackgroundColor is Android-only — no iOS equivalent needed.
    await StatusBar.setOverlaysWebView({ overlay: true });
  } catch {}

  try {
    // None = WebView stays fullscreen; keyboard overlays. Keeps the
    // bottom mobile nav anchored when any input gains focus.
    await Keyboard.setResizeMode({ mode: KeyboardResize.None });
    await Keyboard.setScroll({ isDisabled: false });

    // We listen to BOTH the Will and Did events because some Android
    // @capacitor/keyboard versions only emit Did*, while iOS emits both.
    // Setting the same value twice is idempotent — cheaper than a
    // platform branch and avoids the "Android only fires Did*" trap.
    Keyboard.addListener('keyboardWillShow', (info) => keyboard.setPlugin(info.keyboardHeight));
    Keyboard.addListener('keyboardDidShow', (info) => keyboard.setPlugin(info.keyboardHeight));
    Keyboard.addListener('keyboardWillHide', () => keyboard.setPlugin(0));
    Keyboard.addListener('keyboardDidHide', () => keyboard.setPlugin(0));
  } catch {}

  // Guard the App plugin explicitly: in some iOS WebView contexts the
  // Capacitor bridge reports a native platform (so `isNative()` is true)
  // while the @capacitor/app plugin isn't registered. Calling it then
  // returns a rejected promise that surfaces as an unhandled rejection
  // (Sentry MUCKA-WEB-8, hit on the public /q/:shortId share page).
  // `isPluginAvailable` + a defensive `.catch` keep it fail-safe.
  if (Capacitor.isPluginAvailable('App')) {
    App.addListener('backButton', ({ canGoBack }) => {
      // A sheet is not a page in the history: Back closes it first, through
      // its own Escape, so an edited form still asks before losing anything.
      // It used to go back a page under the sheet, form and all.
      if (backButtonAction(sheetStackDepth()) === 'close-sheet') {
        document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
        return;
      }
      if (canGoBack || window.history.length > 1) {
        router.back();
      } else {
        App.exitApp();
      }
    }).catch(() => {});

    App.addListener('appUrlOpen', (event) => {
      try {
        const url = new URL(event.url);
        const path = url.pathname + url.search + url.hash;
        window.location.assign(path);
      } catch {}
    }).catch(() => {});

    // Apple requirement 1.5 — warm Tap to Pay up on every foreground, not only
    // at launch. iOS tears the reader session down while the app is in the
    // background, so an engineer who checks a text on a doorstep and comes back
    // would otherwise pay the full cold-connect cost with the customer stood
    // there. No-ops on anything that is not a set-up iPhone; see warmUpTapToPay.
    App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) void warmUpTapToPay();
    }).catch(() => {});
  }

  setTimeout(() => {
    SplashScreen.hide({ fadeOutDuration: 300 }).catch(() => {});
  }, 200);

  // Live Activity push-to-start observer. iOS-only, no-ops elsewhere.
  // Best-effort: a failure here shouldn't prevent the app from booting.
  void setupPushToStartTokens();

  // Reconcile any active store subscription with the server (self-heals a
  // missed IAP webhook / reinstall / new-device sign-in). Best-effort.
  void reconcileEntitlementOnLaunch();

  // The launch half of requirement 1.5. `appStateChange` fires on foreground
  // transitions, not on a cold start, so without this the very first sheet
  // after opening the app is the slow one.
  void warmUpTapToPay();
}

/**
 * Tell the server this is the app on EVERY request, navigations included.
 *
 * 🚨 The header above rides only `fetch`, and the `MuckaApp` UA token was
 * meant to cover navigations — but on Android a navigation answered by the
 * service worker goes out with the WebView's DEFAULT user agent, token-less.
 * The server then took the app for Chrome: Settings, then Subscribe, bounced
 * straight back off /paywall, and a locked owner was routed to the web billing
 * tab instead of the paywall. A cookie is sent on a service-worker fetch where
 * the UA override is not. Written on every launch, so a binary that gains the
 * purchase plugin updates it. Server side: `native-guard.ts`.
 */
function stampNativeAppCookie(): void {
  if (typeof document === 'undefined') return;
  const p = platform();
  if (p !== 'ios' && p !== 'android') return;
  const value = nativeAppCookieValue(p, hasIapPlugin());
  document.cookie = `${NATIVE_APP_COOKIE}=${value}; path=/; max-age=${60 * 60 * 24 * 365}; SameSite=Lax; Secure`;
}

function injectPlatformHeader(): void {
  if (typeof window === 'undefined') return;
  const originalFetch = window.fetch.bind(window);
  const tag = platform(); // 'ios' | 'android' | 'web' — only called when isNative()
  const iapCapable = hasIapPlugin();

  window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;

    // Only tag same-origin requests. Cross-origin (e.g. Supabase REST,
    // Stripe Checkout) must not carry our internal header — some upstreams
    // reject unexpected headers and CORS preflights get expensive fast.
    let sameOrigin = true;
    try {
      sameOrigin = new URL(url, window.location.origin).origin === window.location.origin;
    } catch {
      sameOrigin = false;
    }
    if (!sameOrigin) return originalFetch(input, init);

    const headers = new Headers(init?.headers || (typeof input !== 'string' && !(input instanceof URL) ? input.headers : undefined));
    headers.set('X-Mucka-Platform', tag);
    // Signal IAP capability so the server serves the in-app-purchase experience
    // only to binaries that actually ship the plugin (release gate).
    if (iapCapable) headers.set('X-Mucka-Iap', '1');
    return originalFetch(input, { ...init, headers });
  };
}
